import {
    forwardRef,
    ForwardRefExoticComponent,
    ReactNode,
    RefAttributes,
} from 'react';
import Dialog, { DialogProps } from '@mui/material/Dialog';
import { SxProps, Theme, useTheme } from '@mui/material';
import { SlotProvider } from '@upheal/design-system/utils/SlotContext';
import { Slots } from '@upheal/design-system/utils/Slots';
import { capitalise } from '@upheal/design-system/utils/stringUtils';

import { ModalSizingVariant } from './Modal.styleUtils';
import { ModalHeader } from './ModalHeader';
import { ModalContent } from './ModalContent';
import { ModalFooter } from './ModalFooter';
import { ModalActionButtons } from './ModalActionButtons';

const modalSizeMaxWidths: Record<ModalSizingVariant, number> = {
    sm: 360,
    md: 440,
};

const MARGIN_SM = 12;
const TOP_MARGIN_XS = 16;

const createModalMaxWidthStyle = (
    size: ModalSizingVariant,
    maxWidth: number,
) => ({
    [`& .MuiDialog-paperWidth${capitalise(size)}`]: {
        maxWidth,
    },
});

/**
 * Overrides the default max-width values for the modal sizes.
 * This is necessary because MUI Dialog component uses fixed values for max-width,
 * which do not match our design system.
 */
const overrideMaxWidthValuesStyles = Object.fromEntries(
    Object.entries(modalSizeMaxWidths).map(([size, value]) => [
        size,
        createModalMaxWidthStyle(size as ModalSizingVariant, value),
    ]),
);

export interface ModalProps {
    /**
     * Modal ID, used for accessibility purposes to distinguish multiple modals.
     */
    id: string;

    /**
     * If true, the modal is open
     */
    open: boolean;
    /**
     * Callback fired when the modal requests to be closed by click to the overlay
     * or pressing the Escape key.
     */
    onClose?: VoidFunction;

    /**
     * Close of dialog might take some time due to animation. This callback
     * is called when the close animation ends.
     */
    onCloseEnd?: VoidFunction;

    /**
     * Modal sizing affects padding of the modal content, header, footer,
     *  font size of header titles and footer action buttons.
     * When `maxWidth` props is not passed it also determines the max-width of the modal.
     * @default md
     */
    sizing?: ModalSizingVariant;

    /**
     * If set, it overrides the max-width set by `sizing`, useful when
     * we need to have a custom modal width.
     */
    maxWidth?: number;

    children: ReactNode;

    sx?: SxProps<Theme>;

    slotProps?: Omit<DialogProps['slotProps'], 'paper'>;

    fullScreen?: boolean;
}

type ModalComponent = ForwardRefExoticComponent<
    ModalProps & RefAttributes<HTMLDivElement>
> & {
    Header: typeof ModalHeader;
    Content: typeof ModalContent;
    Footer: typeof ModalFooter;
    ActionButtons: typeof ModalActionButtons;
};

export const Modal = forwardRef<HTMLDivElement, ModalProps>(function Modal(
    {
        id,
        open,
        onClose,
        onCloseEnd,
        sizing = 'md',
        maxWidth,
        children,
        sx,
        slotProps,
        fullScreen,
    },
    ref,
) {
    const theme = useTheme();
    const slots: Slots = {
        modal: {
            id,
            sizing,
        },
    };

    return (
        <SlotProvider value={slots}>
            <Dialog
                open={open}
                onClose={onClose}
                fullWidth
                maxWidth={sizing}
                ref={ref}
                sx={{
                    ...(maxWidth === undefined
                        ? overrideMaxWidthValuesStyles[sizing]
                        : createModalMaxWidthStyle(sizing, maxWidth)),

                    ...sx,
                }}
                slotProps={{
                    ...slotProps,
                    paper: {
                        sx: {
                            [theme.breakpoints.down('md')]: {
                                margin: `${MARGIN_SM}px`,
                                width: `calc(100% - ${MARGIN_SM * 2}px)`,
                            },
                            [theme.breakpoints.down('sm')]: {
                                margin: 0,
                                width: '100%',
                                height: 'fit-content',
                                maxHeight: `calc(100% - ${TOP_MARGIN_XS}px)`,
                            },
                        },
                    },
                }}
                aria-labelledby={`${id}-title`}
                aria-describedby={`${id}-subtitle`}
                onTransitionExited={onCloseEnd}
                fullScreen={fullScreen}
            >
                {children}
            </Dialog>
        </SlotProvider>
    );
}) as ModalComponent;

Modal.Header = ModalHeader;
Modal.Content = ModalContent;
Modal.Footer = ModalFooter;
Modal.ActionButtons = ModalActionButtons;
